import React, { useCallback, useMemo } from 'react';

import { FollowersIcon } from 'components/svg/dashboard/FollowersIcon';
import { StatisticContainer } from '../StatisticContainer';
import { gql } from 'urql';
import { useAuthenticatedMutation, useAuthenticatedQuery } from '../../../client/hooks';

import { useAppState } from 'client/providers/AppProvider';
import {
    Espin_Follow_Type_Enum,
    FollowDocument,
    FollowMutation,
    FollowMutationVariables,
    GetFollowersForHighSchoolsDocument,
    GetFollowersForHighSchoolsQuery,
    GetFollowersForHighSchoolsQueryVariables,
    UnfollowDocument,
    UnfollowMutation,
    UnfollowMutationVariables,
} from '../../../generated/spin-graphql';
import { HighSchoolFollowCard } from '../follows/HighSchoolFollowCard';
gql`
    query GetFollowersForHighSchools($highSchoolId: uuid!, $date: timestamptz!) {
        following: espin_follow(
            where: { high_school_id: { _eq: $highSchoolId }, follow_type: { _eq: high_school_follows_college } }
        ) {
            college_id
        }
        espin_follow(
            where: {
                high_school_id: { _eq: $highSchoolId }
                follow_type: { _eq: college_follows_high_school }
                college_user: { deleted_at: { _is_null: true } }
            }
        ) {
            ...HighSchoolFollowCard
        }
        espin_follow_aggregate(
            where: {
                high_school_id: { _eq: $highSchoolId }
                follow_type: { _eq: college_follows_high_school }
                created_at: { _gt: $date }
                college_user: { deleted_at: { _is_null: true } }
            }
        ) {
            aggregate {
                count
            }
        }
    }
`;

type Props = {};
const backUpDate = new Date().toISOString();
export const HighSchoolFollowers: React.FC<Props> = ({}) => {
    const { user } = useAppState();
    const { isReady, data } = useAuthenticatedQuery<
        GetFollowersForHighSchoolsQuery,
        GetFollowersForHighSchoolsQueryVariables
    >(
        {
            query: GetFollowersForHighSchoolsDocument,
            variables: {
                highSchoolId: user?.high_school_profile?.high_school.id,
                date: user.last_login ?? backUpDate,
            },
            pause: !user,
        },
        ['espin_follow'],
    );

    const [__, unFollow] = useAuthenticatedMutation<UnfollowMutation, UnfollowMutationVariables>(UnfollowDocument);

    const [_, follow] = useAuthenticatedMutation<FollowMutation, FollowMutationVariables>(FollowDocument);

    const handleUnfollowClicked = useCallback(
        (collegeId) => {
            unFollow(
                {
                    where: {
                        college_id: { _eq: collegeId },
                        high_school_id: { _eq: user?.high_school_profile?.high_school?.id },
                        follow_type: { _eq: Espin_Follow_Type_Enum.HighSchoolFollowsCollege },
                    },
                },
                { additionalTypenames: ['espin_follow'] },
            );
        },
        [unFollow, user?.high_school_profile?.high_school?.id],
    );

    const handleFollowClicked = useCallback(
        (collegeId) => {
            follow(
                {
                    input: {
                        college_id: collegeId,
                        high_school_id: user?.high_school_profile?.high_school?.id,
                    },
                },
                { additionalTypenames: ['espin_follow'] },
            );
        },
        [follow, user?.high_school_profile?.high_school?.id],
    );

    const handleFollowOrUnfollow = useCallback(
        (userId: string, following: boolean) => {
            if (following) {
                handleUnfollowClicked(userId);
            } else {
                handleFollowClicked(userId);
            }
        },
        [handleFollowClicked, handleUnfollowClicked],
    );

    const listOfFollows = useMemo(() => data?.following.map((f) => f.college_id), [data?.following]);

    return (
        <>
            {isReady && (
                <div className="follow-container">
                    <StatisticContainer
                        label={'Total Followers'}
                        icon={<FollowersIcon />}
                        value={`${data?.espin_follow.length}`}
                    />
                    <StatisticContainer
                        label={'New Followers'}
                        icon={<FollowersIcon />}
                        value={`${data?.espin_follow_aggregate.aggregate.count}`}
                    />
                    <HighSchoolFollowCard
                        following={listOfFollows}
                        followers={data.espin_follow}
                        noContentIcon={<FollowersIcon />}
                        noContentLabel={'You have no followers'}
                        handleFollowButtonClicked={handleFollowOrUnfollow}
                    />
                </div>
            )}
        </>
    );
};
